import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";

import CloseIcon from "@mui/icons-material/Close";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { ClientInterface, SchedulingInterface } from "../../common/interfaces";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  SchedulingStatusLabel,
  TypeSchedulingStatus
} from "../enum/schedulingStatusEnum";
import PageToaster from "../toaster/PageToaster";

interface SchedulingCreateModalProps {
  open: boolean;
  onClose: () => void;
  scheduling?: SchedulingInterface | null;
}

const SchedulingCreateModal: React.FC<SchedulingCreateModalProps> = ({
  open,
  onClose,
  scheduling
}) => {
  const { isUser } = useAuth();

  const [description, setDescription] = useState("");
  const [statusValue, setStatusValue] = useState<TypeSchedulingStatus>(
    TypeSchedulingStatus.NONE
  );
  const [clientId, setClientId] = useState<number | "">("");
  const [clientsList, setClientsList] = useState<ClientInterface[]>([]);

  const [isUpdate, setIsUpdate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [schedulingDate, setSchedulingDate] = useState<Date | null>(new Date());

  const containerId = "toaster-scheduling-create-modal";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const handleHttpError = makeToasterErrorHandler(autoError);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const resp = await api.get("/client", {
          params: { page: 1, limit: 100, showActives: true }
        });
        setClientsList(resp.data.items);
      } catch (err) {
        console.error("Erro ao buscar clientes:", err);
      }
    };
    fetchClients();
  }, []);

  const clear = () => {
    setIsUpdate(false);
    setSchedulingDate(null);
    setDescription("");
    setStatusValue(TypeSchedulingStatus.NONE);
    setClientId("");
  };

  // Pré‐preencher campos para edição
  useEffect(() => {
    if (scheduling) {
      setIsUpdate(true);
      setSchedulingDate(new Date(scheduling.scheduling_date));
      setDescription(scheduling.scheduling_description);
      setStatusValue(scheduling.scheduling_status);
      setClientId(scheduling.client_id ?? "");
    } else {
      clear();
    }
  }, [scheduling]);

  const handleClose = () => {
    clear();
    onClose();
  };

  const handleSubmit = async () => {
    if (!schedulingDate) {
      autoError("Selecione data e hora do agendamento.");
      return;
    }
    if (description.trim() === "") {
      autoError("Preencha a descrição do agendamento.");
      return;
    }
    if (statusValue === TypeSchedulingStatus.NONE) {
      autoError("Selecione um status válido.");
      return;
    }

    setIsSubmitting(true);
    const payload = {
      scheduling_date: schedulingDate.toISOString(),
      scheduling_description: description,
      scheduling_status: statusValue, // ex: "CONFIRMED", "PENDING" etc.
      client_id: clientId || null
    };

    try {
      if (isUpdate && scheduling) {
        await api.put(`/scheduling/${scheduling.scheduling_id}`, payload);
        autoSuccess("Agendamento atualizado com sucesso!");
      } else {
        await api.post("/scheduling", payload);
        autoSuccess("Agendamento criado com sucesso!");
      }

      clear();
      onClose();
    } catch (err: any) {
      console.error(
        "Erro ao salvar agendamento:",
        err.response?.data || err.message[0]
      );
      handleHttpError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={handleClose} aria-labelledby="modal-scheduling">
        <Box
          sx={{
            position: "absolute" as const,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
            width: { xs: "90vw", sm: "500px" },
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              {isUpdate ? "Editar Agendamento" : "Novo Agendamento"}
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={ptBR}
          >
            <DateTimePicker
              label="Data e Hora do Agendamento"
              value={schedulingDate}
              onChange={setSchedulingDate}
              format="dd/MM/yyyy HH:mm"
              minDate={new Date()}
              viewRenderers={{
                hours: renderTimeViewClock,
                minutes: renderTimeViewClock,
                seconds: renderTimeViewClock
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "normal"
                }
              }}
            />
          </LocalizationProvider>

          <TextField
            fullWidth
            label="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            multiline
            minRows={2}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel id="label-status">Status</InputLabel>
            <Select
              labelId="label-status"
              value={statusValue}
              label="Status"
              onChange={(e) =>
                setStatusValue(e.target.value as TypeSchedulingStatus)
              }
            >
              {(Object.values(TypeSchedulingStatus) as TypeSchedulingStatus[])
                .filter(
                  (code) => code !== TypeSchedulingStatus.NONE // remove o “”
                )
                .map((statusCode) => (
                  <MenuItem key={statusCode} value={statusCode}>
                    {SchedulingStatusLabel[statusCode]}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Seleção de Cliente — opcional: dá pra agendar um evento sem
              vincular a nenhum cliente. */}
          <FormControl fullWidth margin="normal">
            <InputLabel id="label-client">Cliente (opcional)</InputLabel>
            <Select
              labelId="label-client"
              value={clientId}
              label="Cliente (opcional)"
              onChange={(e) => {
                const value = e.target.value as number | "";
                setClientId(value === "" ? "" : Number(value));
              }}
            >
              <MenuItem value="">
                <em>Nenhum cliente</em>
              </MenuItem>
              {clientsList.map((cli) => (
                <MenuItem key={cli.client_id} value={cli.client_id}>
                  {cli.client_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Botões de ação */}
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button onClick={handleClose} color="secondary" sx={{ mr: 2 }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              // disabled={isSubmitting || isUser}
            >
              {isUpdate ? "Atualizar" : "Cadastrar"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default SchedulingCreateModal;
